import {BehaviorSubject, Observable} from 'rxjs';
import {CollectionViewer, DataSource} from '@angular/cdk/collections';
import {finalize} from 'rxjs/operators';
import {RestSearchUtil} from '@shared/utils/rest-search-util';
import {BaseApi, BaseModel, ListFilter, ListResult, Pagination, SearchFieldGroup, SearchResult} from "@shared/base";

export class BaseDatasource<M extends BaseModel> implements DataSource<M> {
  private dataSubject = new BehaviorSubject<M[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  public records: number;

  constructor(
    private baseApi: BaseApi<M>,
    private searchBy: "filter" | "request"
  ) {
  }

  public get loading$(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }

  public get subject$(): Observable<M[]> {
    return this.dataSubject.asObservable();
  }

  public get data(): M[] {
    return this.dataSubject.getValue();
  }

  connect(_collectionViewer: CollectionViewer): Observable<M[]> {
    return this.dataSubject.asObservable();
  }

  disconnect(_collectionViewer: CollectionViewer): void {
    this.dataSubject.complete();
    this.loadingSubject.complete();
  }

  load(filter: SearchFieldGroup | ListFilter, pagination: Pagination) {
    this.loadingSubject.next(true);

    if (this.searchBy === 'filter') {
      this.loadByFilter(filter as ListFilter, pagination);
    } else {
      this.loadByRequest(filter as SearchFieldGroup, pagination);
    }
  }

  private loadByFilter(filter: ListFilter, pagination: Pagination) {
    this.baseApi.findByFilter(filter, pagination).pipe(
      finalize(() => this.loadingSubject.next(false))
    ).subscribe((value: ListResult<M>) => {
      this.dataSubject.next(value.data);
      this.records = value.records
    });
  }

  private loadByRequest(filter: SearchFieldGroup, pagination: Pagination) {
    const searchRequest = RestSearchUtil.createMatSearchRequest(pagination, filter);
    this.baseApi.findByRequest(searchRequest).pipe(
      finalize(() => this.loadingSubject.next(false))
    ).subscribe((listResult: SearchResult<M>) => {
      this.dataSubject.next(listResult.data);
      this.records = listResult.records;
    });
  }
}
